/**
 * "Is this AI-proposed tier really a SIBLING calibre?" heuristics.
 *
 * The enrichCalibre model has been observed proposing calibres from the same
 * family — Unitas 6498-2 under 6498-1, Valjoux 7753 under 7750 — as if they
 * were tier variants of the base. They are not: a tier shares the base's
 * mechanical architecture and differs only in finishing, decoration,
 * regulation grade or precious materials; a sibling is a separate calibre
 * whose beat rate, escapement, hairspring, balance type or complication
 * layout differs. Filed as a tier, a sibling silently inherits fields that
 * were never true of it, and the curator gets no signal that the model
 * confused a lineage split with a grade upgrade.
 *
 * This module is the client-side backstop: when the AI proposal's overrides
 * touch two or more mechanical signals, or its name carries a
 * generation suffix, the CustomCalibreModal shows an amber warning telling
 * the curator this looks like a sibling and to consider creating it as a
 * separate calibre. Non-blocking on purpose — the curator can still accept
 * the tier if they know better. The prompt itself carries the primary rule
 * (with `complicationLayout` as a fifth signal, since the AI has that in
 * its knowledge but the tier-override schema does not carry it); this file
 * covers the four signals the override schema actually exposes.
 */

import type { AIProposedCalibreTier } from '../../services/firebase';

/**
 * Which mechanical signals the AI proposal's overrides touch. A signal
 * "differs from base" when the tier's overrides carry a value for it —
 * the tier-override contract is "include a field ONLY when this tier's
 * value differs from the base calibre" (see the enrichCalibre prompt),
 * so presence IS difference by construction.
 *
 * Kept as its own field-key type rather than `keyof CalibreTierOverrides`:
 * the sibling-signal set is a deliberate subset of the schema (four fields
 * out of ~thirty) and pinning it locally means adding a new override field
 * to the schema doesn't silently expand what triggers the amber warning.
 */
export type SiblingSignalKey =
  | 'powerReserveHours'
  | 'escapement'
  | 'hairspring'
  | 'balanceType';

/** Human labels for the four signals — used in the amber warning body. */
export const SIBLING_SIGNAL_LABELS: Record<SiblingSignalKey, string> = {
  powerReserveHours: 'power reserve',
  escapement: 'escapement',
  hairspring: 'hairspring',
  balanceType: 'balance type',
};

/**
 * The signal keys, in a fixed order — the amber warning lists them in this
 * order, which mirrors the order of importance in the prompt (beat-rate
 * proxy first, escapement second, balance components after).
 */
export const SIBLING_SIGNAL_KEYS: SiblingSignalKey[] = [
  'powerReserveHours',
  'escapement',
  'hairspring',
  'balanceType',
];

/** True when a single override slot carries a non-empty value. */
function overrideCarriesValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  return true;
}

/**
 * Which of the four sibling signals this proposal touches. Read from the
 * proposal's `overrides` alone — the base calibre's own values do NOT
 * change the answer, because a tier override present at all IS a claim of
 * difference by the tier-override contract.
 */
export function differingSiblingSignals(
  proposal: Pick<AIProposedCalibreTier, 'overrides'>,
): SiblingSignalKey[] {
  const overrides = proposal.overrides ?? {};
  return SIBLING_SIGNAL_KEYS.filter((key) =>
    overrideCarriesValue((overrides as Record<string, unknown>)[key]),
  );
}

/**
 * A tier-name suffix that looks like a calibre-generation marker, not a
 * grade name. Handles the three common shapes:
 *
 *   `-1`, `-2`, `-3`, ... — Unitas 6498-1 / 6498-2, ETA 2892-A2, etc.
 *   `A`, `B`, ...          — single-letter generation, after a separator
 *   `II`, `III`, `IV`, ... — roman numerals up to VIII, after a separator
 *
 * Only after a separator (space, hyphen, slash or opening bracket) — a
 * bare trailing letter that happens to match is part of the tier's own
 * name (e.g. a hypothetical grade called "Top A" is fine; "Top-A" is
 * suspect). The match is case-insensitive for the letter and roman
 * variants; numeric suffixes need no case handling.
 */
/**
 * Two independent tests, either one qualifies:
 *
 *  1. `CALIBRE_REF_SHAPED` — the tier name contains 3+ consecutive digits.
 *     Real grade names in the enrichCalibre vocabulary ("Elaboré", "Top",
 *     "Chronometer", "Grand Luxe") never carry digits, so any tier NAMED
 *     with a calibre reference ("7753", "6498-2", "2892-A2") is already
 *     smelly — the model has typed a sibling calibre's reference into the
 *     tier-name slot.
 *
 *  2. `GENERATION_SUFFIX` — a short token after a separator ("Cal III",
 *     "P.9000 II", "RR1102-C"). Covers roman numerals I..VIII, single
 *     letters, and single letters trailed by digits ("A2"). A separator is
 *     required so a bare short input ("II" alone, "A" alone) does not
 *     trip the check — that would be corrupt input, not a "base + suffix"
 *     name.
 */
const CALIBRE_REF_SHAPED = /\d{3,}/;
const GENERATION_SUFFIX = /[\s\-/(](?:\d+|[A-Z]\d*|I{1,3}|IV|V|VI{0,3}|VIII)$/i;

export function hasGenerationSuffix(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  return CALIBRE_REF_SHAPED.test(trimmed) || GENERATION_SUFFIX.test(trimmed);
}

export interface SiblingWarningVerdict {
  /** True when the amber warning should render for this proposal. */
  showWarning: boolean;
  /** The mechanical signals whose overrides carry a value. */
  differingSignals: SiblingSignalKey[];
  /** True when the tier name looks like a calibre-generation marker. */
  nameLooksLikeGeneration: boolean;
}

/**
 * Combined verdict: show the amber warning when EITHER trigger fires.
 *
 * Two signals differing OR a generation-suffixed name — both are strong
 * enough on their own that gating one on the other would suppress
 * exactly the cases the warning exists to catch (Valjoux 7753 differs
 * on chronograph subdial layout, which the override schema does not
 * carry, so the ONLY client-visible signal there is often the "-3"-ish
 * name; conversely, a "Top" grade that happens to carry three
 * mechanical overrides is a real red flag whether the name looks
 * suspect or not).
 */
export function siblingWarningVerdict(
  proposal: Pick<AIProposedCalibreTier, 'name' | 'overrides'>,
): SiblingWarningVerdict {
  const differingSignals = differingSiblingSignals(proposal);
  const nameLooksLikeGeneration = hasGenerationSuffix(proposal.name);
  return {
    showWarning: differingSignals.length >= 2 || nameLooksLikeGeneration,
    differingSignals,
    nameLooksLikeGeneration,
  };
}
